import { useState, useEffect } from "react";
import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  RefreshCw,
  Sparkles,
  Wand2,
  X,
  XCircle,
} from "lucide-react";
import {
  useApplySectionSuggestions,
  type ManualEvaluation,
  type Section,
} from "@/hooks/use-proposals";
import { DiffPreviewModal } from "./diff-preview-modal";

/**
 * Mejora M (changelog cliente): panel inline que muestra el resultado de
 * una evaluación manual de sección y ofrece "Aplicar sugerencias con IA".
 *
 * Se renderiza solo si la sección tiene `last_manual_evaluation`. Si
 * `manual_evaluation_stale` es true (el contenido cambió tras la
 * evaluación), muestra un badge "Desactualizada" para que el usuario
 * sepa que el score no refleja el contenido actual.
 */

const DIMENSION_LABELS: Record<string, { label: string; weightLabel: string }> = {
  requisitos_tecnicos: { label: "Requisitos técnicos", weightLabel: "40%" },
  citacion_fuentes: { label: "Citación de fuentes", weightLabel: "30%" },
  estructura_formato: { label: "Estructura y formato", weightLabel: "20%" },
  tono_profesional: { label: "Tono profesional", weightLabel: "10%" },
};

const DIMENSION_ORDER = [
  "requisitos_tecnicos",
  "citacion_fuentes",
  "estructura_formato",
  "tono_profesional",
];

function scoreColor(score: number): string {
  if (score >= 0.8) return "text-green-700";
  if (score >= 0.6) return "text-yellow-700";
  return "text-red-700";
}

function scoreBgColor(score: number): string {
  if (score >= 0.8) return "bg-green-500";
  if (score >= 0.6) return "bg-yellow-500";
  return "bg-red-500";
}

function formatScore(score: number): string {
  return `${Math.round(score * 100)}%`;
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

interface SectionEvaluationPanelProps {
  proposalId: string;
  section: Section;
  evaluation: ManualEvaluation;
  stale: boolean;
  /** Lanza una nueva evaluación (botón "Reevaluar"). */
  onReevaluate: () => void;
  isReevaluating: boolean;
  /**
   * Callback cuando el usuario acepta el contenido refinado. Recibe el
   * HTML para insertar en el editor; el padre se encarga de persistir
   * con `useUpdateSection` (auto-save) o forzar guardado.
   */
  onApplyRefined: (refinedHtml: string) => void;
}

export function SectionEvaluationPanel({
  proposalId,
  section,
  evaluation,
  stale,
  onReevaluate,
  isReevaluating,
  onApplyRefined,
}: SectionEvaluationPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const applyMutation = useApplySectionSuggestions(proposalId);
  // HTML refinado pendiente de revisión en el modal de diff (null = cerrado).
  const [pendingRefinedHtml, setPendingRefinedHtml] = useState<string | null>(null);

  // Human-in-the-loop: el usuario marca (checkbox) y puede editar QUÉ
  // sugerencias aplicar. Por defecto NINGUNA marcada → selección deliberada.
  const [selectedKeys, setSelectedKeys] = useState<Record<string, boolean>>({});
  const [editedText, setEditedText] = useState<Record<string, string>>({});
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  // Sugerencias ya aplicadas en esta evaluación (feedback visible "Aplicada").
  const [appliedKeys, setAppliedKeys] = useState<Set<string>>(new Set());

  // Al llegar una evaluación NUEVA (reevaluar) los gaps cambian: reseteamos
  // selección, ediciones y "aplicadas" para no arrastrar estado obsoleto.
  useEffect(() => {
    setSelectedKeys({});
    setEditedText({});
    setAppliedKeys(new Set());
    setEditingKey(null);
  }, [evaluation.id, evaluation.timestamp]);

  // Listas planas con key estable. Separamos HUECOS (gaps) de SUGERENCIAS
  // (mejoras opcionales) con prefijos distintos para que sus keys no choquen.
  const buildItems = (field: "gaps" | "suggestions", prefix: string) =>
    DIMENSION_ORDER.flatMap((dimKey) => {
      const dim = evaluation.dimensions?.[dimKey];
      const arr = (dim as { [k: string]: unknown } | undefined)?.[field];
      if (!Array.isArray(arr)) return [];
      return (arr as string[]).map((text, idx) => ({
        key: `${prefix}:${dimKey}:${idx}`,
        dimKey,
        original: text,
      }));
    });
  const allGaps = buildItems("gaps", "gap");
  const allSuggestions = buildItems("suggestions", "sug");
  const allItems = [...allGaps, ...allSuggestions];

  const itemText = (key: string, original: string) =>
    editedText[key] ?? original;
  // Solo las NO aplicadas son seleccionables (las aplicadas quedan marcadas).
  const selectableItems = allItems.filter((g) => !appliedKeys.has(g.key));
  const selectedCount = selectableItems.filter(
    (g) => selectedKeys[g.key]
  ).length;
  const allSelected =
    selectableItems.length > 0 && selectedCount === selectableItems.length;

  const toggleKey = (key: string) =>
    setSelectedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  const setAll = (value: boolean) =>
    setSelectedKeys(
      Object.fromEntries(selectableItems.map((g) => [g.key, value]))
    );
  const saveEdit = (key: string) => {
    const v = editingValue.trim();
    if (v) setEditedText((prev) => ({ ...prev, [key]: v }));
    setEditingKey(null);
  };

  const score = evaluation.score;
  const Icon =
    score >= 0.8 ? CheckCircle2 : score >= 0.6 ? AlertTriangle : XCircle;
  const iconColor =
    score >= 0.8
      ? "text-green-600"
      : score >= 0.6
      ? "text-yellow-600"
      : "text-red-600";

  const hasActionableItems = allItems.length > 0;

  // Umbral SOLO informativo: por encima de este score mostramos una pista de
  // "calidad alta". NO bloquea reevaluar ni aplicar — el usuario decide si
  // quiere seguir refinando (feedback del cliente: quitar el límite del 88%).
  const HIGH_QUALITY_THRESHOLD = 0.88;
  const isHighQuality = score >= HIGH_QUALITY_THRESHOLD && !stale;

  const handleApply = () => {
    // Solo los items marcados (huecos o sugerencias, no aplicados), con su
    // texto editado. El backend aplica igual ambos tipos.
    const selected_suggestions = selectableItems
      .filter((g) => selectedKeys[g.key])
      .map((g) => ({ dimension: g.dimKey, text: itemText(g.key, g.original) }));
    if (selected_suggestions.length === 0) return;
    applyMutation.mutate(
      {
        sectionId: section.id,
        evaluation_id: evaluation.id,
        selected_suggestions,
      },
      {
        onSuccess: (refined) => {
          // En vez de aplicar directamente (machacando el contenido),
          // abrimos el modal de diff para que el usuario revise y decida.
          setPendingRefinedHtml(refined.content_html);
        },
      }
    );
  };

  // Fila de un item (hueco o sugerencia): checkbox + texto editable + estado.
  const renderItemRow = (item: {
    key: string;
    dimKey: string;
    original: string;
  }) => {
    const { key, dimKey, original } = item;
    const isEditing = editingKey === key;
    const isApplied = appliedKeys.has(key);
    const text = itemText(key, original);
    const dimLabel = DIMENSION_LABELS[dimKey]?.label ?? dimKey;
    return (
      <li key={key} className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={!!selectedKeys[key]}
          onChange={() => toggleKey(key)}
          disabled={isApplied}
          className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 disabled:opacity-50"
          aria-label={`Seleccionar: ${text}`}
        />
        {isEditing ? (
          <div className="flex-1 flex items-center gap-1">
            <input
              autoFocus
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveEdit(key);
                if (e.key === "Escape") setEditingKey(null);
              }}
              className="flex-1 px-1.5 py-0.5 text-xs border border-purple-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-400"
            />
            <button
              type="button"
              onClick={() => saveEdit(key)}
              className="p-0.5 text-emerald-600 hover:bg-emerald-50 rounded"
              aria-label="Guardar sugerencia"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setEditingKey(null)}
              className="p-0.5 text-gray-400 hover:bg-gray-100 rounded"
              aria-label="Cancelar edición"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex-1 flex items-start justify-between gap-1 group">
            <span className="text-xs">
              <span className="mr-1 text-[10px] uppercase tracking-wide text-gray-400">
                {dimLabel}:
              </span>
              <span
                className={
                  isApplied
                    ? "text-gray-400 line-through"
                    : selectedKeys[key]
                    ? "text-gray-800"
                    : "text-gray-600"
                }
              >
                {text}
              </span>
              {editedText[key] && !isApplied && (
                <span className="ml-1 text-[10px] text-purple-600">
                  (editada)
                </span>
              )}
              {isApplied && (
                <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600 align-middle">
                  <Check className="w-2.5 h-2.5" />
                  Aplicada
                </span>
              )}
            </span>
            {!isApplied && (
              <button
                type="button"
                onClick={() => {
                  setEditingKey(key);
                  setEditingValue(text);
                }}
                className="p-0.5 text-gray-300 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Editar sugerencia"
              >
                <Pencil className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <>
    <div
      className={`rounded-lg border ${
        stale ? "border-amber-200 bg-amber-50/30" : "border-blue-200 bg-blue-50/30"
      } overflow-hidden`}
    >
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/50 transition-colors text-left"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-3 flex-wrap">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
          <Sparkles className="w-4 h-4 text-purple-600" />
          <span className="text-sm font-medium text-gray-900">
            Evaluación de la sección
          </span>
          <span className={`text-sm font-semibold ${scoreColor(score)}`}>
            <Icon className={`inline w-4 h-4 mr-1 ${iconColor}`} />
            {formatScore(score)}
          </span>
          <span className="text-xs text-gray-500">
            {formatTimestamp(evaluation.timestamp)}
          </span>
          {stale && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800">
              <AlertCircle className="w-3 h-3" />
              Nota desactualizada
            </span>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-gray-200 bg-white">
          {stale && (
            <div className="flex items-start gap-2 mt-3 p-2.5 rounded-md bg-amber-50 border border-amber-200">
              <AlertCircle className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
              <div className="flex-1 text-xs text-amber-900">
                {appliedKeys.size > 0 && (
                  <p className="font-medium text-emerald-700 mb-0.5">
                    ✓ {appliedKeys.size} sugerencia
                    {appliedKeys.size === 1 ? "" : "s"} aplicada
                    {appliedKeys.size === 1 ? "" : "s"} al texto.
                  </p>
                )}
                <p>
                  Esta puntuación (<strong>{formatScore(score)}</strong>) es de{" "}
                  <strong>antes de tus últimos cambios</strong> (al aplicar
                  sugerencias o editar el texto). Reevalúa para ver la nota
                  actual.
                </p>
                <button
                  type="button"
                  onClick={onReevaluate}
                  disabled={isReevaluating}
                  className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isReevaluating ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5" />
                  )}
                  Reevaluar ahora
                </button>
              </div>
            </div>
          )}

          {hasActionableItems && (
            <div className="flex items-center justify-between pt-3">
              <p className="text-xs text-gray-500">
                Marca lo que quieras aplicar (puedes editarlo):
              </p>
              <button
                type="button"
                onClick={() => setAll(!allSelected)}
                className="text-[11px] font-medium text-purple-600 hover:text-purple-800"
              >
                {allSelected ? "Quitar selección" : "Seleccionar todo"}
              </button>
            </div>
          )}

          {/* Dimensiones: solo la puntuación (los items van en bloques aparte) */}
          <div className="space-y-2">
            {DIMENSION_ORDER.map((dimKey) => {
              const dim = evaluation.dimensions?.[dimKey];
              if (!dim) return null;
              const meta = DIMENSION_LABELS[dimKey];
              return (
                <div key={dimKey}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-700">
                      {meta.label}{" "}
                      <span className="text-gray-400">({meta.weightLabel})</span>
                    </span>
                    <span className={`font-semibold ${scoreColor(dim.score)}`}>
                      {formatScore(dim.score)}
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${scoreBgColor(dim.score)} transition-all`}
                      style={{ width: `${Math.round(dim.score * 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Huecos a corregir: lo que FALTA o está flojo (baja el score) */}
          {allGaps.length > 0 && (
            <div className="pt-1">
              <h4 className="flex items-center gap-1.5 text-xs font-semibold text-amber-800 mb-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                Huecos a corregir ({allGaps.length})
              </h4>
              <ul className="space-y-1">{allGaps.map(renderItemRow)}</ul>
            </div>
          )}

          {/* Sugerencias de mejora: el contenido YA lo cubre; son opcionales */}
          {allSuggestions.length > 0 && (
            <div className="pt-1">
              <h4 className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 mb-1">
                <Sparkles className="w-3.5 h-3.5" />
                Sugerencias de mejora — opcional ({allSuggestions.length})
              </h4>
              <p className="text-[11px] text-gray-500 mb-1.5">
                El contenido ya cubre estos puntos; son mejoras que puedes aplicar
                si quieres, no huecos.
              </p>
              <ul className="space-y-1">{allSuggestions.map(renderItemRow)}</ul>
            </div>
          )}

          {evaluation.feedback && (
            <div className="border-t border-gray-100 pt-2">
              <p className="text-xs font-medium text-gray-500 mb-1">
                Resumen del crítico:
              </p>
              <p className="text-xs text-gray-700 italic">{evaluation.feedback}</p>
            </div>
          )}

          {/* Acciones */}
          <div className="border-t border-gray-100 pt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onReevaluate}
              disabled={isReevaluating || applyMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isReevaluating ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              {stale ? "Reevaluar con el contenido actual" : "Reevaluar"}
            </button>

            <button
              type="button"
              onClick={handleApply}
              disabled={
                applyMutation.isPending ||
                isReevaluating ||
                !hasActionableItems ||
                selectedCount === 0
              }
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                !hasActionableItems
                  ? "El crítico no detectó gaps accionables"
                  : selectedCount === 0
                  ? "Marca al menos una sugerencia para aplicar"
                  : "El LLM aplica SOLO las sugerencias que has marcado"
              }
            >
              {applyMutation.isPending ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Aplicando...
                </>
              ) : (
                <>
                  <Wand2 className="w-3.5 h-3.5" />
                  {selectedCount > 0
                    ? `Aplicar ${selectedCount} sugerencia${
                        selectedCount === 1 ? "" : "s"
                      } con IA`
                    : "Aplicar sugerencias con IA"}
                </>
              )}
            </button>

            {isHighQuality ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-800">
                <CheckCircle2 className="w-3 h-3" />
                Calidad alta ({Math.round(score * 100)}%) — seguir refinando es
                opcional
              </span>
            ) : !hasActionableItems ? (
              <span className="text-xs text-gray-500 italic">
                Sin sugerencias accionables.
              </span>
            ) : null}
          </div>
        </div>
      )}
    </div>

    {pendingRefinedHtml !== null && (
      <DiffPreviewModal
        currentHtml={section.content_html ?? ""}
        refinedHtml={pendingRefinedHtml}
        onReject={() => setPendingRefinedHtml(null)}
        onAccept={() => {
          // Marcar como aplicadas las sugerencias seleccionadas (feedback
          // "Aplicada") y limpiar la selección, para que se vea claramente
          // qué entró en el texto.
          const justApplied = selectableItems
            .filter((g) => selectedKeys[g.key])
            .map((g) => g.key);
          setAppliedKeys((prev) => {
            const next = new Set(prev);
            justApplied.forEach((k) => next.add(k));
            return next;
          });
          setSelectedKeys({});
          onApplyRefined(pendingRefinedHtml);
          setPendingRefinedHtml(null);
        }}
      />
    )}
    </>
  );
}

export default SectionEvaluationPanel;
